// app/frontend/components/EventForm/hooks/useEventValidation.tsx

import { useCallback } from 'react';
import type { EventData } from '../types';

interface UseEventValidationProps {
  isDraft: boolean;
  editor: any; // TipTap editor instance
  event?: EventData;
}

export const useEventValidation = ({ isDraft, editor, event }: UseEventValidationProps) => {
  // URL validation helper
  const validateURL = useCallback((input: string): boolean => {
    const urlPattern = /^(http:\/\/|https:\/\/)/i;
    return urlPattern.test(input);
  }, []);

  // Date validation helper
  const validateDateRange = useCallback((startDate: string, endDate: string, startTime: string, endTime: string) => {
    if (!startDate || !endDate || !startTime || !endTime) return null;

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      return 'End date and time must be after start date and time';
    }
    return null;
  }, []);

  // Rich text validation helper
  const validateDescription = useCallback(
    (value: string): string | null => {
      if (isDraft) return null;

      if (editor) {
        const plainText = editor.getText().trim();
        if (plainText.length < 20) {
          return 'Description must be at least 20 characters long';
        }
        if (plainText.length > 5000) {
          return 'Description must be less than 5000 characters';
        }
      }
      return null;
    },
    [isDraft, editor],
  );

  // Array validation helper
  const validateArrayField = useCallback(
    (arr: any[], fieldName: string, minItems = 1): string | null => {
      if (isDraft) return null;

      if (!Array.isArray(arr) || arr.length < minItems) {
        return `At least ${minItems} ${fieldName.replace('_', ' ')} ${minItems === 1 ? 'is' : 'are'} required`;
      }
      return null;
    },
    [isDraft],
  );

  // Complex slug validation
  const validateSlug = useCallback((slug: string, name: string): string | null => {
    if (!slug) return 'Slug is required';

    // Basic format validation
    const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugPattern.test(slug)) {
      return 'Slug must contain only lowercase letters, numbers, and hyphens';
    }

    // Length validation
    if (slug.length < 3) return 'Slug must be at least 3 characters long';
    if (slug.length > 100) return 'Slug must be less than 100 characters';

    // Reserved words check
    const reservedWords = ['new', 'edit', 'create', 'admin', 'api', 'www'];
    if (reservedWords.includes(slug)) {
      return 'This slug is reserved and cannot be used';
    }

    return null;
  }, []);

  // Image validation
  const validateEventImage = useCallback(
    (imageFile: File | null, existingImageUrl?: string): string | null => {
      if (isDraft) return null;

      // Check if we have either a new image or existing image
      const hasImage = imageFile || (event?.id && existingImageUrl);
      if (!hasImage) {
        return 'Please upload an event image';
      }

      // Validate new image file if provided
      if (imageFile) {
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (imageFile.size > maxSize) {
          return 'Image file size must be less than 5MB';
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(imageFile.type)) {
          return 'Image must be in JPEG, PNG, or WebP format';
        }
      }

      return null;
    },
    [isDraft, event?.id],
  );

  // Time validation for same day events
  const validateSameDayTimes = useCallback(
    (startTime: string, endTime: string, spansNextDay: boolean): string | null => {
      if (!startTime || !endTime || spansNextDay) return null;

      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes <= startMinutes) {
        return 'End time must be after start time when event is on the same day';
      }

      return null;
    },
    [],
  );

  // Recurring event validation
  const validateRecurringEvent = useCallback(
    (isRecurring: boolean, occurrences: any[], attributes: any): string | null => {
      if (!isRecurring || isDraft) return null;

      if (!occurrences || occurrences.length === 0) {
        return 'At least one occurrence is required for recurring events';
      }

      if (occurrences.length > 52) {
        return 'Maximum 52 occurrences allowed';
      }

      if (!attributes.recurrence_pattern) {
        return 'Recurrence pattern is required';
      }

      return null;
    },
    [isDraft],
  );

  // Main validation rules object
  const validationRules = {
    // Basic required fields
    name: (v: string | undefined) => {
      if (!v?.trim()) return 'Event name is required';
      if (v.trim().length < 3) return 'Event name must be at least 3 characters';
      if (v.trim().length > 200) return 'Event name must be less than 200 characters';
      return null;
    },

    slug: (v: string | undefined, values: any) => validateSlug(v || '', values.name),

    description: (v: string | undefined) => validateDescription(v || ''),

    // Address validation
    address: (v: string | undefined, values: any) => {
      if (values.online_event === 'true') return null;
      if (!v?.trim()) return 'Address is required for in-person events';
      if (v.trim().length < 10) return 'Please provide a complete address';
      return null;
    },

    // Timezone validation
    timezone: (v: string | string[] | undefined) => {
      const timezone = Array.isArray(v) ? v[0] : v;
      if (!timezone) return 'Timezone is required';
      return null;
    },

    // Date and time validation
    event_start_date: (v: string | undefined, values: any) => {
      if (values.is_recurring === 'true') return null;
      if (!v) return 'Event start date is required';

      // Check if date is in the past (only for new events)
      if (!event?.id) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eventDate = new Date(v);

        if (eventDate < today) {
          return 'Event date cannot be in the past';
        }
      }

      return null;
    },

    event_start_time: (v: string | undefined, values: any) => {
      if (values.is_recurring === 'true') return null;
      if (!v) return 'Event start time is required';
      return null;
    },

    event_end_date: (v: string | undefined, values: any) => {
      if (!v || !values.event_start_date) return null;

      const startDate = new Date(values.event_start_date);
      const endDate = new Date(v);

      if (endDate < startDate) {
        return 'Event end date must be the same as or after the start date';
      }

      return null;
    },

    event_end_time: (v: string | undefined, values: any) => {
      if (!v) return null;

      // Validate time range
      const dateRangeError = validateDateRange(
        values.event_start_date,
        values.event_end_date,
        values.event_start_time,
        v,
      );
      if (dateRangeError) return dateRangeError;

      // Validate same day times
      if (values.event_start_date === values.event_end_date) {
        return validateSameDayTimes(values.event_start_time, v, values.spans_next_day === 'true');
      }

      return null;
    },

    // Frequency validation
    event_frequency_id: (v: string | undefined) => {
      if (isDraft) return null;
      if (!v) return 'Event frequency is required';
      return null;
    },

    // Array field validations
    event_type_ids: (v: any[] | undefined) => validateArrayField(v || [], 'event_type', 1),
    dance_style_ids: (v: any[] | undefined) => validateArrayField(v || [], 'dance_style', 1),

    // URL validations
    website_url: (v: string | undefined) =>
      v && !validateURL(v) ? 'Enter a valid URL starting with http:// or https://' : null,
    ticket_url: (v: string | undefined) =>
      v && !validateURL(v) ? 'Enter a valid URL starting with http:// or https://' : null,
    facebook_url: (v: string | undefined) =>
      v && !validateURL(v) ? 'Enter a valid URL starting with http:// or https://' : null,
    instagram_url: (v: string | undefined) =>
      v && !validateURL(v) ? 'Enter a valid URL starting with http:// or https://' : null,
    meetup_url: (v: string | undefined) =>
      v && !validateURL(v) ? 'Enter a valid URL starting with http:// or https://' : null,
    tiktok_url: (v: string | undefined) =>
      v && !validateURL(v) ? 'Enter a valid URL starting with http:// or https://' : null,

    // Image validation
    event_image: (v: File | null | undefined, values: any) => validateEventImage(v || null, values.existing_image_url),

    // Recurring event validation
    recurring_event_occurrences: (v: any[] | undefined, values: any) => {
      return validateRecurringEvent(values.is_recurring === 'true', v || [], values.recurring_event_attributes);
    },

    // Organizer validation (if user is required to select organizers)
    organizer_ids: (v: any[] | undefined, values: any) => {
      // Only validate if the user doesn't have a default organizer
      if (isDraft) return null;
      // Add your organizer validation logic here if needed
      return null;
    },
  };

  // Validation helper functions for external use
  const validateForm = useCallback(
    (values: any) => {
      const errors: Record<string, string> = {};

      Object.entries(validationRules).forEach(([field, validator]) => {
        const error = validator(values[field], values);
        if (error) {
          errors[field] = error;
        }
      });

      return {
        hasErrors: Object.keys(errors).length > 0,
        errors,
      };
    },
    [validationRules],
  );

  // Real-time validation for specific fields
  const validateField = useCallback(
    (fieldName: string, value: any, allValues: any) => {
      const validator = validationRules[fieldName as keyof typeof validationRules];
      if (!validator) return null;

      return validator(value, allValues);
    },
    [validationRules],
  );

  return {
    validationRules,
    validateForm,
    validateField,
    validateURL,
    validateDateRange,
    validateDescription,
    validateSlug,
    validateEventImage,
  };
};
